import { Appointment, BookingConfirmation, PartialService, Service, createEmptyAppointment } from '../types';
import { dayjs } from '../utils/dayjs-config';

const RECENT_BOOKING_STORAGE_KEY_PREFIX = 'recentBooking';
const RECENT_BOOKING_MAX_AGE_DAYS = 90;

const getRecentBookingStorageKey = (tenantId: string): string =>
  `${RECENT_BOOKING_STORAGE_KEY_PREFIX}:${tenantId}`;

export type RecentBookingSnapshot = {
  tenantId: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhoneNumber: string;
  employeeID: string;
  servicesList: PartialService[];
  appointmentTitle: string;
  appointmentDuration: number;
  billingAmount: number;
  bookedAt?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  notes?: string;
};

const isRecentBookingSnapshot = (value: unknown): value is RecentBookingSnapshot => {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as RecentBookingSnapshot;
  return (
    typeof snapshot.tenantId === 'string' &&
    snapshot.tenantId.length > 0 &&
    typeof snapshot.customerFirstName === 'string' &&
    typeof snapshot.customerLastName === 'string' &&
    typeof snapshot.customerEmail === 'string' &&
    typeof snapshot.customerPhoneNumber === 'string' &&
    typeof snapshot.employeeID === 'string' &&
    Array.isArray(snapshot.servicesList) &&
    typeof snapshot.appointmentTitle === 'string' &&
    typeof snapshot.appointmentDuration === 'number' &&
    typeof snapshot.billingAmount === 'number' &&
    (snapshot.bookedAt === undefined || typeof snapshot.bookedAt === 'string') &&
    (snapshot.appointmentDate === undefined || typeof snapshot.appointmentDate === 'string') &&
    (snapshot.appointmentTime === undefined || typeof snapshot.appointmentTime === 'string')
  );
};

const isExpiredRecentBooking = (
  snapshot: RecentBookingSnapshot,
  referenceTime = dayjs.tz(),
): boolean => {
  if (!snapshot.bookedAt) return true;
  const bookedAt = dayjs(snapshot.bookedAt);
  if (!bookedAt.isValid()) return true;
  return referenceTime.diff(bookedAt, 'day', true) > RECENT_BOOKING_MAX_AGE_DAYS;
};

export const loadRecentBookingSnapshot = (tenantId: string): RecentBookingSnapshot | null => {
  try {
    const storageKey = getRecentBookingStorageKey(tenantId);
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      !isRecentBookingSnapshot(parsed) ||
      parsed.tenantId !== tenantId ||
      isExpiredRecentBooking(parsed)
    ) {
      window.localStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('Failed to read recent booking snapshot', error);
    return null;
  }
};

export const saveRecentBookingSnapshot = (snapshot: RecentBookingSnapshot): void => {
  try {
    window.localStorage.setItem(getRecentBookingStorageKey(snapshot.tenantId), JSON.stringify(snapshot));
  } catch (error) {
    console.warn('Failed to save recent booking snapshot', error);
  }
};

export const clearRecentBookingSnapshot = (tenantId: string): void => {
  try {
    window.localStorage.removeItem(getRecentBookingStorageKey(tenantId));
  } catch (error) {
    console.warn('Failed to clear recent booking snapshot', error);
  }
};

export const buildRecentBookingSnapshot = (
  booking: BookingConfirmation,
  tenantId: string,
): RecentBookingSnapshot => ({
  tenantId,
  customerFirstName: booking.customerFirstName,
  customerLastName: booking.customerLastName,
  customerEmail: booking.customerEmail,
  customerPhoneNumber: booking.customerPhoneNumber,
  employeeID: booking.employeeID,
  servicesList: booking.servicesList ?? [],
  appointmentTitle: booking.appointmentTitle,
  appointmentDuration: booking.appointmentDuration,
  billingAmount: booking.billingAmount,
  bookedAt: dayjs.tz().toISOString(),
  appointmentDate: booking.appointmentDate,
  appointmentTime: booking.appointmentTime,
  notes: booking.notes,
});

// Hide the rebook prompt when the last booking is within the past hour.
export const isWithinOneHourOfBooking = (
  snapshot: RecentBookingSnapshot | null,
  referenceTime = dayjs.tz()
): boolean => {
  if (!snapshot?.bookedAt) return false;
  const bookedAt = dayjs(snapshot.bookedAt);
  if (!bookedAt.isValid()) return false;
  return Math.abs(referenceTime.diff(bookedAt, 'hour', true)) < 1;
};

export const buildAppointmentFromSnapshot = (
  snapshot: RecentBookingSnapshot,
  services: Service[]
): Appointment => {
  const serviceById = new Map(services.map((service) => [service.id, service]));
  const resolvedServices = snapshot.servicesList.map((service) => {
    const liveService = serviceById.get(service.id);
    if (!liveService) return service;
    return {
      id: liveService.id,
      serviceTitle: liveService.title,
      serviceTime: liveService.time,
      serviceAmount: liveService.priceVaries ? 0 : liveService.price,
      ...(liveService.priceVaries ? { priceVaries: true } : {}),
    };
  });
  // "Price varies" services are priced at the visit: never reuse the last
  // booking's total (which may hold that visit's final price).
  const priceToConfirm = resolvedServices.some((service) => service.priceVaries === true);

  const appointmentDuration =
    resolvedServices.reduce((sum, service) => sum + service.serviceTime, 0) || snapshot.appointmentDuration;
  const knownTotal = resolvedServices.reduce((sum, service) => sum + service.serviceAmount, 0);
  const billingAmount = priceToConfirm ? knownTotal : knownTotal || snapshot.billingAmount;
  const appointmentTitle =
    resolvedServices.map((service) => service.serviceTitle).join(', ') || snapshot.appointmentTitle;

  return createEmptyAppointment({
    employeeID: snapshot.employeeID,
    billingAmount,
    appointmentDuration,
    appointmentTitle,
    servicesList: resolvedServices,
    ...(priceToConfirm ? { priceToConfirm: true } : {}),
    notes: snapshot.notes ?? '',
    customerFirstName: snapshot.customerFirstName,
    customerLastName: snapshot.customerLastName,
    customerEmail: snapshot.customerEmail,
    customerPhoneNumber: snapshot.customerPhoneNumber,
  });
};
